/**
 * GUEST CODES — one printed QR per placement, destination resolved at scan time.
 *
 * Firestore:
 *   {locationDocPath}/guest_codes/{guid}
 *   guest_code_tokens/{token}   (top-level pointer; doc id IS the printed token)
 *
 * A code stores a TARGET, never a URL (except `customUrl`, where the url IS the
 * target). Domains, slugs, loyalty join tokens and review links change; paper
 * does not. The resolver in @order-up/core turns a target into a destination on
 * every scan. See docs/superpowers/specs/2026-09-14-guest-qr-platform-design.md §2.
 */

export const GUEST_CODE_TARGET_TYPES = [
  "website",
  "menu",
  "ordering",
  "table",
  "loyaltyJoin",
  "googleReview",
  "social",
  "wifi",
  "itemFeedback",
  "customUrl",
] as const;
export type GuestCodeTargetType = (typeof GUEST_CODE_TARGET_TYPES)[number];

/**
 * Types an operator may create from the hub. `table` is absent: a table code is
 * minted from the floor plan together with the table page it opens, never as a
 * free-standing code pointing at a page that does not exist.
 */
export const GUEST_CODE_CREATABLE_TARGET_TYPES = GUEST_CODE_TARGET_TYPES.filter(
  (type): type is Exclude<GuestCodeTargetType, "table"> => type !== "table",
);

export const GUEST_CODE_PLACEMENTS = [
  "table",
  "counter",
  "window",
  "receipt",
  "bag",
  "flyer",
  "menuBoard",
  "studioDesign",
  "other",
] as const;
export type GuestCodePlacement = (typeof GUEST_CODE_PLACEMENTS)[number];

export const GUEST_CODE_STATUSES = ["active", "retired"] as const;
export type GuestCodeStatus = (typeof GUEST_CODE_STATUSES)[number];

export const GUEST_CODE_SOCIAL_PLATFORMS = ["facebook", "instagram", "tiktok", "twitter"] as const;
export type GuestCodeSocialPlatform = (typeof GUEST_CODE_SOCIAL_PLATFORMS)[number];

export const GUEST_WIFI_SECURITY = ["WPA", "WEP", "nopass"] as const;
export type GuestWifiSecurity = (typeof GUEST_WIFI_SECURITY)[number];

export const GUEST_CODE_LABEL_MAX_LENGTH = 80;
export const GUEST_CODE_URL_MAX_LENGTH = 2048;

export interface IGuestCodeTarget {
  readonly type: GuestCodeTargetType;
  /** `table` only. */
  readonly tableGuid?: string;
  /** `social` only. */
  readonly socialPlatform?: GuestCodeSocialPlatform;
  /** `itemFeedback` only — a feedback ROUND or CARD guid. */
  readonly feedbackGuid?: string;
  /** `customUrl` only — https, validated. */
  readonly url?: string;
}

export interface IGuestCode {
  readonly guid: string;
  readonly tenantDocId: string;
  readonly restaurantDocId: string;
  /** Printed bearer value. Never in a list/detail projection. */
  readonly token: string;
  readonly label: string;
  readonly placement: GuestCodePlacement;
  readonly target: IGuestCodeTarget;
  readonly status: GuestCodeStatus;
  /** Lifetime scans (bots excluded). */
  readonly scanCount: number;
  /** Set when this code was replaced by a rotation. */
  readonly replacedByGuid?: string;
  readonly createdAt?: unknown;
  readonly updatedAt?: unknown;
  readonly retiredAt?: unknown;
  readonly createdByUid?: string;
  readonly updatedByUid?: string;
}

/** `guest_code_tokens/{token}` — the scope a scan needs, readable by doc id. */
export interface IGuestCodeToken {
  readonly tenantDocId: string;
  readonly restaurantDocId: string;
  readonly codeGuid: string;
  readonly status: GuestCodeStatus;
  readonly createdAt?: unknown;
}

/** Guest-facing wifi. NOT the infrastructure NetworkConfig SSID/PSK. */
export interface IGuestWifiSettings {
  readonly ssid: string;
  readonly password: string;
  readonly security: GuestWifiSecurity;
}

function isOneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === "string" && (allowed as readonly string[]).includes(value);
}

function trimmedString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

/** `https:` only, and parseable. Anything else is not a destination we print. */
export function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length > GUEST_CODE_URL_MAX_LENGTH) return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * The target, reduced to exactly the fields its type uses. `null` when the type
 * is unknown or a field the type requires is missing/invalid — a stored target
 * that normalizes to null resolves as unavailable, never as a guess.
 */
export function normalizeGuestCodeTarget(value: unknown): IGuestCodeTarget | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  if (!isOneOf(raw.type, GUEST_CODE_TARGET_TYPES)) return null;
  const type = raw.type;

  switch (type) {
    case "table": {
      const tableGuid = trimmedString(raw.tableGuid);
      return tableGuid ? { type, tableGuid } : null;
    }
    case "social":
      return isOneOf(raw.socialPlatform, GUEST_CODE_SOCIAL_PLATFORMS)
        ? { type, socialPlatform: raw.socialPlatform }
        : null;
    case "itemFeedback": {
      const feedbackGuid = trimmedString(raw.feedbackGuid);
      return feedbackGuid ? { type, feedbackGuid } : null;
    }
    case "customUrl": {
      const url = trimmedString(raw.url);
      return url && isHttpsUrl(url) ? { type, url } : null;
    }
    default:
      return { type };
  }
}

export function toGuestCodeStatus(value: unknown): GuestCodeStatus {
  // Unknown reads as retired: a code whose status cannot be read must not keep
  // counting scans as if it were live.
  return isOneOf(value, GUEST_CODE_STATUSES) ? value : "retired";
}

export function toGuestCodePlacement(value: unknown): GuestCodePlacement {
  return isOneOf(value, GUEST_CODE_PLACEMENTS) ? value : "other";
}

/** The management projection. The token is stripped HERE, not at call sites. */
export function projectGuestCodeForManagement(code: IGuestCode): Omit<IGuestCode, "token"> {
  const { token: _omitted, ...rest } = code;
  return rest;
}
